// Communicator Documentation:
// https://docs.techsoft3d.com/communicator/latest/build/api_ref/typedoc/classes/communicator.model.html#setnodesvisibilities

import { Component } from "react";

import Toolbar from "./Toolbar";
import Loader from "./Loader";

import ScriptLoader from "../../../utils/ScriptLoader";
import { BoundingBox } from "../../../schema/generated/schema";
import {
  HoopsWrapper,
  Container,
  Name,
  Dimensions,
} from "./HoopsViewer.styled";
import { getAuthHeadersFromCookie } from "../../../utils/generalUtils";

function getFileNameTokens(fileName: string): string[] {
  const regex = /(.*)(\..*)$/;
  const tokens = regex.exec(fileName) || [];
  tokens.shift();
  return tokens;
}

const IN_MM_CONVERSION_FACTOR = 25.4;

const getPartDimensions = (
  box: BoundingBox,
  boxUnits: "MM" | "IN",
  inputUnits: "MM" | "IN"
) => {
  let { x, y, z } = box;

  if (inputUnits !== boxUnits) {
    if (inputUnits === "MM") {
      // convert box inches to MM
      x *= IN_MM_CONVERSION_FACTOR;
      y *= IN_MM_CONVERSION_FACTOR;
      z *= IN_MM_CONVERSION_FACTOR;
    } else {
      // convert box MM to inches
      x /= IN_MM_CONVERSION_FACTOR;
      y /= IN_MM_CONVERSION_FACTOR;
      z /= IN_MM_CONVERSION_FACTOR;
    }
  }

  if (inputUnits === "MM") {
    return `${x.toFixed(2)}mm x ${y.toFixed(2)}mm x ${z.toFixed(2)}mm`;
  }

  return `${x.toFixed(3)}in x ${y.toFixed(3)}in x ${z.toFixed(3)}in`;
};

const truncateFileName = (fileName: string, length: number) => {
  if (fileName.length < length) {
    return fileName;
  }

  const tokens = getFileNameTokens(fileName);
  const name = tokens[0];
  const ext = tokens[1];
  return (
    name.substr(0, length / 2) +
    String.fromCharCode(8230) +
    name.substr(name.length - length / 2) +
    ext
  );
};

interface Props {
  url: string;
  fileReplacing?: string;
  solidModelType: string;
  onModelLoaded?: () => void;
  box?: BoundingBox | null;
  boxUnit?: "MM" | "IN" | null;
  originalFilename: string;
  wwwOrigin: string;
}

interface State {
  viewer: Communicator.WebViewer | null;
  maxZoom: number;
  currentZoom: number;
  defaultFaceColor: Communicator.Color | null;
  defaultLineColor: Communicator.Color | null;
  backgroundTop: Communicator.Color | null;
  backgroundBottom: Communicator.Color | null;
}

class HoopsViewer extends Component<Props, State> {
  loader: Loader | null = null;

  originalFetchFunction = window.fetch;

  constructor(props) {
    super(props);

    this.state = {
      viewer: null,
      currentZoom: 1,
      maxZoom: 50,
      defaultFaceColor: null,
      defaultLineColor: null,
      backgroundTop: null,
      backgroundBottom: null,
    };
    this.loader = null;
  }

  componentDidMount() {
    const { url: partFileUrl } = this.props;
    this.initViewer(partFileUrl);

    const { fetch: originalFetch } = window;

    window.fetch = async (...args) => {
      // eslint-disable-next-line
      let [resource, config] = args;

      if (args[0] === partFileUrl) {
        const accessTokenCookie = getAuthHeadersFromCookie().Authorization;

        config = {
          ...config,
          credentials: "include",
          headers: {
            ...config?.headers,
            Authorization: accessTokenCookie || "",
          },
        };
      }

      const response = await originalFetch(resource, config);

      return response;
    };
  }

  componentDidUpdate() {
    const { viewer } = this.state;

    if (viewer) {
      viewer.resizeCanvas();
      viewer.redraw();
    }
  }

  componentWillUnmount() {
    window.fetch = this.originalFetchFunction;
  }

  static getDerivedStateFromProps(nextProps, state) {
    const { selectedDfmInfo, selectedDfmSeverity } = nextProps;
    const { viewer } = state;

    if (viewer) {
      return {
        selectedDfmInfo,
        selectedDfmSeverity,
      };
    }
    return {};
  }

  getOperator<T extends Communicator.Operator.Operator>(
    id: Communicator.OperatorId
  ) {
    const { viewer } = this.state;
    return viewer?.operatorManager.getOperator(id) as T;
  }

  customizeLineVisibility = () => {
    const { solidModelType } = this.props;
    const { viewer } = this.state;
    if (viewer && solidModelType === "stl") {
      viewer.view.setLineVisibility(false);
    }
  };

  customizeInputMonitor = () => {
    const { viewer } = this.state;
    if (viewer) {
      const inputMonitor = (viewer as any)._inputMonitor;
      inputMonitor._calculateElementOffset = () => {
        const rect = viewer.getViewElement().getBoundingClientRect();
        inputMonitor._elementOffset = {
          top: rect.top,
          left: rect.left,
        };
      };
      inputMonitor._calculateElementOffset();
    }
  };

  customizeZoomOperator = () => {
    const zoomOperator =
      this.getOperator<Communicator.Operator.CameraZoomOperator>(
        Communicator.OperatorId.Zoom
      );
    if (zoomOperator) {
      const { maxZoom, currentZoom } = this.state;
      zoomOperator.setZoomToMousePosition(false);

      // TODO: is there another way to limit zoom other than overriding zoom functions?
      const oldEvent = (zoomOperator as any)._doZoom.bind(zoomOperator);
      (zoomOperator as any)._doZoom = (scale, ...args) => {
        if (maxZoom > currentZoom || scale > 1) {
          oldEvent(scale, ...args);
        }
      };
    }
  };

  customizeBackgroundColor = () => {
    const { viewer, backgroundTop, backgroundBottom } = this.state;
    if (viewer) {
      viewer.view.setBackgroundColor(backgroundTop, backgroundBottom);
    }
  };

  changeSize = () => {
    const { viewer } = this.state;
    if (viewer) {
      viewer.resizeCanvas();
    }
  };

  onCameraUpdate = (camera: Communicator.Camera) => {
    const eye = Communicator.Point3.subtract(
      camera.getPosition(),
      camera.getTarget()
    );
    this.setState({
      currentZoom: eye.length(),
    });
  };

  measurementValueSet = (
    measurement: Communicator.Markup.Measure.MeasureMarkup
  ) => {
    const { viewer } = this.state;

    if (viewer) {
      // TODO: _angle does not exist on measurement type in new version of web viewer
      // if (!measurement._angle) {
      const measurementValueMM =
        measurement.getMeasurementValue() * measurement.getUnitMultiplier();
      const measurementValueInc = measurementValueMM / 25.4;
      measurement.setMeasurementText(
        `${measurementValueInc.toFixed(3)} in | ${measurementValueMM.toFixed(
          1
        )} mm`
      );
      // }
    }
  };

  modelLoadFailure = (modelName: string, reason: string, error?: any) => {
    console.log("Model load failure", { modelName, reason, error });
  };

  modelLoadBegin = () => {
    if (this.loader) {
      this.loader.reset();
    }
    this.changeSize();
  };

  modelLoaded = () => {
    if (this.loader) {
      this.loader.modelLoaded();
    }
  };

  sceneReady = async () => {
    this.customizeBackgroundColor();
    this.customizeZoomOperator();
    this.customizeInputMonitor();
    this.customizeLineVisibility();
    this.changeSize();
  };

  enableAxisTriad = () => {
    const { viewer } = this.state;

    if (viewer) {
      viewer.view.getAxisTriad().enable();
    }
  };

  enableNavCube = () => {
    const { viewer } = this.state;

    if (viewer) {
      viewer.view.getNavCube().enable();
    }
  };

  modelStructureReady = () => {
    const { viewer, defaultFaceColor, defaultLineColor } = this.state;

    if (!viewer) {
      return;
    }

    const { view, model } = viewer;
    this.onCameraUpdate(view.getCamera());
    view.setViewOrientation(Communicator.ViewOrientation.Iso, 0);
    model.getModelBounding(true, true).then(modelBox => {
      const extend = modelBox.extents();
      const max = Math.max(extend.x, extend.y, extend.z);

      this.setState({
        maxZoom: max * 7,
      });
    });

    const searchNodeTree = async (model: Communicator.Model, child: number) => {
      // Set model face/edge color
      if (defaultFaceColor) {
        model.setNodesFaceColor([child], defaultFaceColor);
      }

      if (defaultLineColor) {
        model.setNodesLineColor([child], defaultLineColor);
      }

      const children = model.getNodeChildren(child);

      if (children.length > 0) {
        const promises = children.map(element =>
          searchNodeTree(model, element)
        );
        return Promise.all(promises);
      }
    };

    const node = model.getAbsoluteRootNode();
    searchNodeTree(model, node);
  };

  onModelLoaded = () => {
    const { onModelLoaded } = this.props;

    if (onModelLoaded) {
      onModelLoaded();
    }
  };

  initViewer = async (url: string) => {
    const { wwwOrigin } = this.props;

    const CommunicatorLoader = new ScriptLoader(
      `${wwwOrigin}/os/proposal/static/js/hoops_web_viewer_monolithic.js`
    );

    await CommunicatorLoader.load();

    this.setState({
      defaultFaceColor: new Communicator.Color(208, 233, 245),
      defaultLineColor: new Communicator.Color(85, 100, 109),
      backgroundTop: new Communicator.Color(243, 246, 248),
      backgroundBottom: new Communicator.Color(222, 224, 225),
    });

    const viewer = new Communicator.WebViewer({
      containerId: "hoopsViewer",
      endpointUri: url,
    });
    viewer.start();
    viewer.setCallbacks({
      modelLoadBegin: this.modelLoadBegin,
      modelLoadFailure: this.modelLoadFailure,
      modelLoaded: this.modelLoaded,
      subtreeLoaded: this.modelLoaded,
      sceneReady: this.sceneReady,
      modelStructureReady: this.modelStructureReady,
      camera: this.onCameraUpdate,
      measurementValueSet: this.measurementValueSet,
    });

    this.setState({ viewer }, () => {
      this.changeSize();
      this.enableNavCube();
      this.enableAxisTriad();
      this.modelLoaded();

      window.addEventListener("resize", () => {
        this.changeSize();
      });
    });
  };

  render() {
    const { viewer } = this.state;
    const { box, boxUnit, fileReplacing, originalFilename } = this.props;
    const toolbar = viewer ? <Toolbar initialized viewer={viewer} /> : null;

    return (
      <HoopsWrapper
        css={{
          margin: "6px -15px",
          height: "100%",
        }}
      >
        <Loader
          ref={loader => {
            this.loader = loader;
          }}
          css={{ height: "calc(100%);" }}
        />
        <Container id="hoopsViewer" />
        <Name title={originalFilename}>
          {fileReplacing ? (
            <em>Replacing file&hellip;</em>
          ) : (
            truncateFileName(originalFilename, 35)
          )}
        </Name>
        {box && boxUnit && (
          <Dimensions>
            {getPartDimensions(box, boxUnit, "IN")} |{" "}
            {getPartDimensions(box, boxUnit, "MM")}
          </Dimensions>
        )}
        {toolbar}
      </HoopsWrapper>
    );
  }
}

export default HoopsViewer;
